import React, { useState } from "react";

const STATUS_STYLE = {
  empty: { color: "var(--color-status-empty)", bg: "var(--color-bg-subtle)" },
  draft: { color: "var(--color-status-draft)", bg: "#fef3e2" },
  live:  { color: "var(--color-status-live)",  bg: "var(--color-accent-subtle)" },
};

/**
 * Publishing model (P1.6):
 *   empty → (no button; auto-transitions to draft when content is saved)
 *   draft → "Publish"
 *   live  → "Unpublish"
 *   live + draftCount > 0 → also show "Publish N records" affordance
 */
export default function StatusFlow({ clientId, sectionType, currentStatus, draftCount = 0, onStatusChange }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const style = STATUS_STYLE[currentStatus] || STATUS_STYLE.empty;

  async function transition(targetStatus) {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/clients/${clientId}/sections/${sectionType}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: targetStatus }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      onStatusChange(data.status, data.draft_record_count ?? 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.wrap}>
      {/* Status pill */}
      <span style={{ ...styles.badge, color: style.color, background: style.bg }}>
        {currentStatus}
      </span>

      {/* Publish when draft */}
      {currentStatus === "draft" && (
        <button style={styles.publishBtn} onClick={() => transition("live")} disabled={saving}>
          {saving ? "Publishing…" : "Publish"}
        </button>
      )}

      {/* Unpublish when live */}
      {currentStatus === "live" && (
        <button style={styles.unpublishBtn} onClick={() => transition("draft")} disabled={saving}>
          {saving ? "Unpublishing…" : "Unpublish"}
        </button>
      )}

      {/* Draft-records affordance — live section has unpublished records */}
      {currentStatus === "live" && draftCount > 0 && (
        <button style={styles.republishBtn} onClick={() => transition("live")} disabled={saving}>
          {saving ? "Publishing…" : `${draftCount} record${draftCount > 1 ? "s" : ""} not visible — Publish`}
        </button>
      )}

      {error && <span style={styles.error}>{error}</span>}
    </div>
  );
}

const styles = {
  wrap: { display: "flex", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap" },
  badge: {
    fontSize: "var(--text-xs)",
    fontWeight: "var(--weight-semibold)",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    borderRadius: "var(--radius-full)",
    padding: "3px var(--space-3)",
  },
  publishBtn: {
    fontSize: "var(--text-sm)",
    fontWeight: "var(--weight-medium)",
    color: "var(--color-text-on-accent)",
    background: "var(--color-accent)",
    border: "none",
    borderRadius: "var(--radius-md)",
    padding: "var(--space-2) var(--space-4)",
    cursor: "pointer",
  },
  unpublishBtn: {
    fontSize: "var(--text-sm)",
    fontWeight: "var(--weight-medium)",
    color: "var(--color-text-secondary)",
    background: "none",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    padding: "var(--space-2) var(--space-4)",
    cursor: "pointer",
  },
  republishBtn: {
    fontSize: "var(--text-xs)",
    fontWeight: "var(--weight-medium)",
    color: "var(--color-status-draft)",
    background: "#fef3e2",
    border: "1px solid var(--color-status-draft)",
    borderRadius: "var(--radius-md)",
    padding: "var(--space-1) var(--space-3)",
    cursor: "pointer",
  },
  error: { fontSize: "var(--text-xs)", color: "var(--color-critical)" },
};
